import { CommonModule, NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  NgSelectComponent,
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgOptgroupTemplateDirective,
  NgNotFoundTemplateDirective,
  NgHeaderTemplateDirective,
} from '@ng-select/ng-select';
import { LOVResponseModel } from '../../../models/lov-response.model';
import { LookupKey } from '../../../constants/lookup-keys';
import { LookupService } from '../../../../core/services/lookup.service';
import { countriesList } from '../../../constants/countries';
import { LovSelectionService } from '../../../../core/services/lov-selection.service';
import { SharedService } from '../../../services/shared.service';
import { Utils } from '../../../../Utils/utils';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { ButtonComponent } from "../../button/button.component";
import { ServiceName, ServiceNameEn } from '../../../constants/services.enum';
@Component({
  selector: 'app-option-field',
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    NgOptgroupTemplateDirective,
    NgNotFoundTemplateDirective,
    NgHeaderTemplateDirective,
    ButtonComponent
  ],
  templateUrl: './option-field.component.html',
  styleUrl: './option-field.component.css',
})
export class OptionFieldComponent implements OnInit, OnChanges, OnDestroy {
  typeahead$ = new Subject<string>();
  // @ViewChild('selectElement') selectElementRef!: ElementRef;
  // @ViewChild('selectElement') selectElementRef!: NgSelectComponent;
  @ViewChild('selectElement', { static: false })
  selectElementRef!: NgSelectComponent;
  @Input() control!: any;
  @Input() label!: string;
  @Input() type: string = 'text';
  @Input() placeholder?: string;
  @Input() errorMessage: string = 'This field is required';
  @Input() options: any = '';
  @Input() otherInfo: any = '';
  @Input() validationMessages: { [key: string]: string } = {};
  @Input() country = false;
  @Input() lovType: string = "";
  @Input() labelKey: string = "Value";
  @Input() valueKey: string = "Value";

  // if value is load based on parent
  @Input() parentControl?: any = "";
  @Input() formSelectedOptions: any = "";
  @Input() parentKey: any = "Value";
  @Input() parentLovType: any = "";
  @Input() loading: boolean = false;
  @Input() showMemberFilter: boolean = false;
  @Input() memberSearchByOptions: any[] | null = null;
  @Input() userType: string = '';
  @Input() isMultiple: boolean = false;
  @Input() allowManualEntry: boolean = false;
  @Input() manualEntryLabel: string = '';
  @Input() manualEntryPlaceholder: string = '';
  @Input() manualEntryToggleLabel: string = 'REGISTER.ENTER_COMPANY_MANUAL';
  @Input() showSelectedTooltip: boolean = true;
  @Input() maxSelectLimit: any = '';


  @Output() onSelectionChange = new EventEmitter();
  @Output() search = new EventEmitter<any>();

  searchBy: string = 'CSN'; // default option
  // { "value": "MemberName", "label": "SEARCH_BY_MEM_NAME" }
  searchByOptions: any = [{ "value": "CSN", "label": "SEARCH_BY_MEM_NUMBER" }, { "value": "LicenseNumber", "label": "SEARCH_BY_MEM_LICENSE" }]
  @ViewChild('ngSelect') ngSelect!: NgSelectComponent;
  private sub?: Subscription;
  private searchDebounce$ = new Subject<string>();
  private searchSub?: Subscription;
  searchText: string = "";
  manualEntryValue: string = "";
  manualEntryEnabled = false;
  get isRequired(): boolean {
    const hasRequired = this.control?.hasValidator?.(Validators.required);
    const hasRequiredTrue = this.control?.hasValidator?.(Validators.requiredTrue);
    return Boolean(hasRequired || hasRequiredTrue);
  }

  // This method is used to initialize the class instance using lookup service, lov service, and shared service
  constructor(
    private lookupService: LookupService,
    private lovService: LovSelectionService,
    private sharedService: SharedService
  ) {

  }
  // This method is used to handle the on changes lifecycle hook using changes
  ngOnChanges(changes: SimpleChanges) {
    if (changes['options'] && this.showMemberFilter && this.searchText) {
      // clear ng-select cached search when new options arrived
      this.selectElementRef?.handleClearClick();
      this.selectElementRef?.detectChanges();
    }
    if (changes['memberSearchByOptions']) {
      this.setDefaultSearchBy();
    }
  }
  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
    this.searchSub = this.searchDebounce$
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((term) => this.emitSearchTerm(term));

    this.setDefaultSearchBy();

    if (this.control && this.lovType == LookupKey.DC_LIC_AUTH) {
      this.sub = this.control.valueChanges.subscribe((value: any) => {
        let object = { lovType: this.lovType, selectedLov: value, list: this.options };
        this.onSelectionChange.emit(object);  // emit whenever value changes
      });
    }
    if (this.parentLovType) {
      this.lovService.getLovSelections().subscribe((lovs) => {
        const parent = lovs
          .slice()
          .reverse()
          .find((x) => x.lovType === this.parentLovType);
        const parentValue = parent?.selectedLov?.[this.parentKey];
        if (parentValue) {
          this.loadOptions(parentValue);
          if (!this.control.value) {
            this.control.setValue(null); // optional: clear existing
          }
        } else {
          if (this.parentControl) {
            this.loadOptions(this.parentControl);
          }
        }
      });
    } else if (!this.parentControl) {
      if (this.lovType) {
        this.loadOptions('');
      }
    }

  }

  get searchByOptionsToUse(): any[] {
    return (this.memberSearchByOptions && this.memberSearchByOptions.length > 0)
      ? this.memberSearchByOptions
      : this.searchByOptions;
  }

  private setDefaultSearchBy(): void {
    const options = this.searchByOptionsToUse;
    if (options?.length) {
      this.searchBy = options[0].value;
    }
  }
  /** Bubble the ng-select search event */
  // This method is used to handle type event using term
  onType(term: any) {
    if (this.manualEntryEnabled) {
      return;
    }
    let searchObj = {
      searchBy: this.searchBy,
      searchText: term
    }
    if (this.showMemberFilter) {
      this.search.emit(searchObj);
    } else {
      this.search.emit(searchObj);
    }
  }

  get shouldShowManualEntry(): boolean {
    if (!this.allowManualEntry || !this.showMemberFilter) {
      return false;
    }
    if (this.manualEntryEnabled) {
      return true;
    }
    if (this.loading) {
      return false;
    }
    const hasSearch = (this.searchText || '').trim().length >= 3;
    const hasOptions = Array.isArray(this.options) && this.options.length > 0;
    return hasSearch && !hasOptions;
  }

  get showManualEntryToggle(): boolean {
    if (!this.allowManualEntry || !this.showMemberFilter) {
      return false;
    }
    if (this.manualEntryEnabled) {
      return true;
    }
    if (this.loading) {
      return false;
    }
    const hasSearch = (this.searchText || '').trim().length >= 3;
    const hasOptions = Array.isArray(this.options) && this.options.length > 0;
    return hasSearch && !hasOptions;
  }


  // This method is used to get country code using country
  getCountryCode(country: any) {
    if (!country) {
      return null;
    }
    return countriesList.find((c) => c.Name.toLowerCase() == country?.toLowerCase())?.Code;
  }
  // This method is used to load options using parent value
  loadOptions(parentValue: string) {
    this.loading = true;
    this.lookupService
      .post<LOVResponseModel>(LookupKey.LOOKUP_URL, {
        ProcessName: "DC Get List Of Values WF",
        Type: this.lovType,
        ParentValue: parentValue ? parentValue : (this.lovType == 'DC_SR_TYPE' ? this.userType : '')
      })
      .subscribe({
        // This method is used to handle the next callback using result
        next: (result: LOVResponseModel) => {
          this.loading = false;
          if (result.SiebelMessage?.['List Of Values'] && result["Error Message"] == null || result["Error Message"] == "") {
            if (this.lovType == LookupKey.SUBJECT) { // option need to be filter based on "Sub Type": "Member"
              let userType = this.sharedService.getPrimaryAccount()['Account Type Code'];
              let lovList = Utils.checkIsArray(result.SiebelMessage['List Of Values']);
              if (userType) {
                this.options = lovList.filter((lov: any) => lov["Sub Type"] == userType);
              } else {
                this.options = lovList;
              }
            } else {
              this.options = Utils.checkIsArray(result.SiebelMessage['List Of Values']);
            }
            let selectedOption: any = "";
            if ((this.control.value || this.control.value?.value != null) && this.options) {
              selectedOption = this.options.find((lov: any) => lov['Value'].toLowerCase() == this.control.value.toLowerCase());
            }
            this.selectionChange(selectedOption, this.options);
          } else {
            this.options = [];
          }
        },
        // This method is used to handle the error callback using e
        error: (e) => {
          this.loading = false;
        }
      });
  }
  // This method is used to error keys
  get errorKeys(): string[] {
    return this.control?.errors ? Object.keys(this.control.errors) : [];
  }

  // This method is used to selection change using e and list
  selectionChange(e: any, list: any = "") {
    let object = { lovType: this.lovType, selectedLov: e, list: list };
    this.onSelectionChange.emit(object);
    if (this.isOptionSelection(e)) {
      this.manualEntryValue = '';
      this.manualEntryEnabled = false;
    }
  }

  getLabel(item: any): string {
    if (!item) {
      return '';
    }
    if (typeof item === 'string') {
      return item;
    }
    //My Application: Special handling for Attestation service type and subtypes 19_10_2026
    if(item?.Name == ServiceNameEn.SIGNATURE_OF_VERIFICATION){
      return ServiceName.ATTESTATION;
    }
    if(item?.Parent == ServiceNameEn.SIGNATURE_OF_VERIFICATION){
      if(item?.Name == ServiceNameEn.ATTESTATION_AUTH_CERTIFICATE){
        return ServiceName.ATTESTATION_AUTH_CERTIFICATE_DISPLAY;
      }
      if(item?.Name == ServiceNameEn.ATTESTATION_CERTIFY_DOCUMENT){
        return ServiceName.ATTESTATION_CERTIFY_DOCUMENT_DISPLAY;
      }
      if(item?.Name == ServiceNameEn.ATTESTATION_VERIFY_SIGNATURE){
        return ServiceName.ATTESTATION_VERIFY_SIGNATURE_DISPLAY;
      }
    }
    // End
    return (
      item?.[this.labelKey] ??
      item?.Value ??
      item?.label ??
      ''
    );
  }

  getTruncatedLabel(item: any, maxLength: number = 90): string {
    const label = this.getLabel(item);
    if (!label || label.length <= maxLength) {
      return label;
    }
    return `${label.slice(0, maxLength).trimEnd()}...`;
  }

  getSelectedTooltip(): string | null {
    if (!this.options) {
      return null;
    }
    if (!this.showSelectedTooltip) {
      return null;
    }
    const value = this.control?.value;
    if (value == null) {
      return null;
    }
    let labelText: string = "";
    let index: any = this.options?.find((o: any) => o[this.labelKey] == value || o[this.valueKey] == value);
    labelText = index ? index[this.labelKey] : "";
    return labelText;
  }

  get isControlReadonly(): boolean {
    return !!this.control?.disabled;
  }

  removeSelectedItem(item: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!this.isMultiple || !this.control || this.isControlReadonly) {
      return;
    }
    const current = Array.isArray(this.control.value) ? [...this.control.value] : [];
    const itemValue = this.resolveComparableValue(item);
    const next = current.filter((value) => this.resolveComparableValue(value) !== itemValue);
    this.control.setValue(next);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.selectionChange(next, this.options);
  }

  /**
   * Allow ng-select to compare previously selected values even when
   * reactive forms hold either primitives or whole objects.
   */
  // This method is used to compare with using option and selected
  compareWith = (option: any, selected: any): boolean => {
    if (option === selected) {
      return true;
    }
    const optionValue = this.resolveComparableValue(option);
    const selectedValue = this.resolveComparableValue(selected);
    return optionValue === selectedValue;
  };

  // This method is used to resolve comparable value using value
  private resolveComparableValue(value: any): unknown {
    if (value == null) {
      return value;
    }

    if (typeof value === 'object') {
      const keysToCheck = [this.valueKey, 'Value', 'value', 'Id', 'id'];
      for (const key of keysToCheck) {
        if (key && value?.[key] != null) {
          return value[key];
        }
      }
    }

    return value;
  }

  private isOptionSelection(value: any): boolean {
    if (value == null) {
      return false;
    }
    if (typeof value === 'object') {
      return true;
    }
    const options = Array.isArray(this.options) ? this.options : [];
    if (!options.length) {
      return false;
    }
    return options.some((option) => this.resolveComparableValue(option) === value);
  }

  // This method is used to handle search by change event
  onSearchByChange() {
    if (!this.searchText || this.searchText.trim().length === 0) {
      return;
    }
    if (this.manualEntryEnabled) {
      return;
    }
    this.search.emit({
      searchBy: this.searchBy,
      searchText: this.searchText
    });
  }
  // This method is used to search member using event
  searchMember(event: Event, trigger: 'input' | 'blur' | 'enter' = 'input') {
    if (trigger === 'enter' && event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    if (this.manualEntryEnabled) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    const rawValue = input?.value ?? '';
    const normalizedValue = rawValue.trim();
    this.searchText = normalizedValue;

    const requiresCompletion =
      this.searchBy === 'CSN' || this.searchBy === 'LicenseNumber';

    if (!requiresCompletion && trigger === 'input') {
      this.emitSearchTerm(normalizedValue);
      return;
    }

    if (requiresCompletion) {
      if (trigger === 'input') {
        this.searchDebounce$.next(normalizedValue);
        return;
      }
      if (trigger === 'blur' || trigger === 'enter') {
        this.emitSearchTerm(normalizedValue);
      }
      return;
    }

    this.emitSearchTerm(normalizedValue);
  }

  // This method is used to focus
  focus() {
    if (this.selectElementRef) {
      this.selectElementRef.focus();
      this.selectElementRef.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  emitSearchTerm(term: string) {
    if (!term || term.length < 3) {
      return;
    }

    this.search.emit({
      searchBy: this.searchBy,
      searchText: term
    });
  }

  onSearchClick() {
    if (this.searchText && this.searchText.trim().length >= 2) {
      this.search.emit({
        searchBy: this.searchBy,
        searchText: this.searchText.trim()
      });
    } else {
      // You can show a message or ignore
    }
  }

  onManualEntryInput(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const value = input?.value ?? '';
    this.manualEntryValue = value;
    this.control?.setValue(value);
    this.control?.markAsDirty();
  }

  onClearClick() {
    this.searchText = '';
  }

  toggleManualEntry(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.manualEntryEnabled = !this.manualEntryEnabled;
    if (!this.manualEntryEnabled) {
      this.manualEntryValue = '';
      this.control?.setValue('');
    } else {
      this.control?.setValue('');
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }
}
